<template>
  <v-dialog
    v-model="dialog"
    persistent
    @input="dialogStateChanged"
    transition="dialog-bottom-transition"
    max-width="800px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        color="primary"
        v-bind="attrs"
        v-on="on"
        id="add-decision-action-button"
      >
        <v-icon>mdi-plus-circle</v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="pa-4 headline white--text primary">
        Add Criteria
      </v-card-title>
      <v-card-text class="pa-0 ma-0">
        <v-tabs v-model="currentTab">
          <v-tab @click="handleSuggestionTab">
            <v-icon left>mdi-playlist-check</v-icon>
            Choose
            <!-- Suggestions <span v-if="decision.category">for <strong>{{decision.category.name}}</strong></span> -->
          </v-tab>
          <v-tab @click="handleCustomTab">
            <v-icon left>mdi-account-details</v-icon>
            Create
          </v-tab>

          <v-tab-item class="pa-4">

            <CriteriaSelect
              :category="decision.category"
              :selectedCriteria="editCriteriaList"
              :selectedItem="selectedCriteriaItem"
              selectMode
              @onAdd="setSelectedCriteria"
              @onLoad="updateSuggestedCriteria"
            />

          </v-tab-item>

          <v-tab-item class="pa-4">

            <form class="d-md-flex align-center">
              <v-text-field
                id="int-tr-trgt-6b"
                class="mr-2"
                v-model="customCriteriaName"
                :error-messages="customCriteriaErrors"
                :counter="60"
                single-line
                dense
                placeholder="New criteria name"
                @input="$v.customCriteriaName.$touch()"
                @blur="$v.customCriteriaName.$touch()"
                >
              </v-text-field>
            </form>

          </v-tab-item>
        </v-tabs>
      </v-card-text>
      <v-card-actions>
        <div v-if="selectedCriteriaItem" class="pl-4">
          Will add {{ selectedCriteriaItem.name }}
        </div>
        <v-spacer></v-spacer>
        <v-btn text @click="dialog = false">Cancel</v-btn>
        <v-btn color="primary" :disabled="(!customCriteriaName || $v.customCriteriaName.$invalid) && !selectedCriteriaItem" @click="handleSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import Constants from '../constants.json'
// This import loads the firebase namespace along with all its type information.
import * as firebase from 'firebase/app'
// Load the auth feature into the firebase namespace.
import 'firebase/auth'
import axios from 'axios'

import guid from '@/mixins/DecidedlyGUID.js'
// vuelidate
import { validationMixin } from 'vuelidate'
import { maxLength, minLength } from 'vuelidate/lib/validators'

// import { mapState } from 'vuex'

// Veux types
// import * as DecisionsTypes from '../store/types/decisions'

import CriteriaSelect from '@/components/CriteriaSelect.vue'

// Custom validations
const notEndingWithColon = (value) => { return !value || value.length === 0 || value.indexOf(':') !== (value.length -1) }

export default {
  name: 'NewDecisionCriteriaDialog',
  mixins: [validationMixin, guid],
  validations: {
    customCriteriaName: {
      minLength: minLength(3),
      maxLength: maxLength(60),
      isUnique(value) {
        // standalone validator ideally should not assume a field is required
        if (!value || value === '') return true

        let existingIndex = -1
        if (this.editCriteriaList) {
          //indexExists = this.editCriteriaList.map(function(c) { return c.name }).indexOf(value)
          existingIndex = this.editCriteriaList.findIndex(item => item.name.toLowerCase() === value.toLowerCase())
        }
        return existingIndex < 0
      },
      notEndingWithColon
    }
  },
  components: {
    CriteriaSelect
  },
  props: {
    editCriteriaList: Array,
    decision: Object
  },
  data () {
    return {
      selectedCriteriaItem: null,
      customCriteriaName: null,
      currentTab: 0,
      dialog: false,
      suggestedCriteria: []
    }
  },
  computed: {
    customCriteriaErrors () {
      const errors = []
      if (!this.$v.customCriteriaName.$dirty) return errors
      !this.$v.customCriteriaName.minLength && errors.push('Criteria name must be at least 3 characters long')
      !this.$v.customCriteriaName.maxLength && errors.push('Criteria name must be at most 60 characters long')
      !this.$v.customCriteriaName.isUnique && errors.push('That name is already in your criteria list')
      !this.$v.customCriteriaName.notEndingWithColon && errors.push('Criteria names cannot end with a colon')
      return errors
    },
  },
  methods: {
    handleSave: async function () {
      if (!this.selectedCriteriaItem && this.customCriteriaName) {
        const currentTaxonLevel = this.decision.category ? this.decision.category.id : 'unassigned'
        const customCriteriaItem = {name: this.customCriteriaName, id: this.genGUID(), taxon: currentTaxonLevel, userGenerated: true}
        this.$emit('onAddCustom', customCriteriaItem)

        const indexInList = this.editCriteriaList.findIndex(item => item.name.toLowerCase() === customCriteriaItem.name.toLowerCase())
        // Only add if it is not already in the list
        if (indexInList === -1) {
          // Check if the name does not match existing suggested criteria
          let matchingSuggestion = null
          if (this.suggestedCriteria) {
            matchingSuggestion = this.suggestedCriteria.find(item => item.name.toLowerCase() === customCriteriaItem.name.toLowerCase())
          }

          if (matchingSuggestion) {
            this.selectedCriteriaItem = matchingSuggestion
          } else {
            // Check if this custom criteria is a synonym for an existing system criteria in the same taxon
            const existingCriteria = await this.getCriteriaIdForUserValue(customCriteriaItem)
            // Set the id and taxon for the custom criteria to the existing criteria with the value as its synonym
            if (existingCriteria) {
              customCriteriaItem.id = existingCriteria.criteriaId
              customCriteriaItem.taxon = existingCriteria.taxon
              customCriteriaItem.userGenerated = false
            }

            // Check the user is not adding criteria which is already in the list with a different name
            const criteriaInList = this.editCriteriaList.find(item => item.id === customCriteriaItem.id)
            if (criteriaInList) {
              // console.log(`Criteria alrady in list as ${criteriaInList.name} rename to ${customCriteriaItem.name}`)
              // Rename the item in the list
              this.$emit('onRename', {id: criteriaInList.id, name: customCriteriaItem.name})
            } else {
              // Add the custom criteria item
              this.selectedCriteriaItem = customCriteriaItem
            }
          }

        }
        // Reset the custom criteria name
        this.customCriteriaName = null
      }

      if (this.selectedCriteriaItem) {
        this.$emit('onAdd', this.selectedCriteriaItem)
      }



      this.dialog = false
    },
    getCriteriaIdForUserValue: async function (customCriteria) {
      if (!customCriteria.name) {
        return null
      }

      const token = firebase.auth().currentUser && (await firebase.auth().currentUser.getIdToken())
      // /categories/:category/criteria/match
      const getURL = `${Constants.API_BASE_PATH}/categories/${customCriteria.taxon}/criteria/match?keyword=${customCriteria.name.toLowerCase()}`
      console.log(`Calling check criteria get ${getURL}`)
      const res = await axios.get(getURL, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (res && res.status === 200 && res.data && res.data.status === 'success') {
        console.log(`Calling check criteria get ${getURL}`)
        return res.data
      } else {
        return null
      }
    },
    setSelectedCriteria: function (criteriaItem) {
      this.selectedCriteriaItem = criteriaItem
    },
    dialogStateChanged: function () {
      this.currentTab = 0
      this.customCriteriaName = null
      this.$v.$reset()
    },
    handleSuggestionTab: function () {
      if (this.currentTab !== 0) {
        this.selectedCriteriaItem = null
        this.customCriteriaName = null
        this.currentTab = 0
      }
    },
    handleCustomTab: function () {
      if (this.currentTab !== 1) {
        this.customCriteriaName = null
        this.selectedCriteriaItem = null
        this.currentTab = 1
      }
    },
    updateSuggestedCriteria: function (suggestedCriteria) {
      this.suggestedCriteria = suggestedCriteria
    },
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">

</style>
